import type { FieldErrors, Resolver } from 'react-hook-form';
import * as Yup from 'yup';
import type { TAutocompleteOption } from '~/components/AutocompleteField/types.ts';
import { blockedDomains } from '~/constants/common.ts';
import { errorMessages } from '~/constants/errorMessages.ts';
import {
  formatDateForDisplay,
  formatDateReverse,
  isValidDateFormat,
  parseDate,
} from '~/helpers/utils.ts';
import { phoneValidation } from '~/helpers/validation.ts';
import {
  GuaranteesBankCommissionsRule,
  GuaranteesDeliveryMethod,
  GuaranteesEffectiveFromType,
  GuaranteesFormRule,
  GuaranteesPrincipalObligationDetails,
  GuaranteesSubordinationRule,
} from '~/store/guarantees/types.ts';
import type {
  TGuaranteesBaseRelations,
  TGuaranteesBaseRelationsValues,
  TGuaranteesBeneficiary,
  TGuaranteesForm,
  TGuaranteesFormValues,
  TGuaranteesPrincipal,
  TPatchGuaranteesBaseRelationsPayload,
  TPatchGuaranteesBeneficiaryPayload,
  TPatchGuaranteesFormPayload,
  TPatchGuaranteesPrincipalPayload,
} from '~/store/guarantees/types.ts';
import {
  BASE_RELATIONS_PROZORRO_VALUE,
  BENEFICIARY_NAME_GUARANTEE_LANG_MAX_LENGTH,
  BENEFICIARY_REQUIRED_FIELDS,
  GUARANTEE_FORM_BANK_COMMISSIONS_RULE_OPTIONS,
  GUARANTEE_FORM_DELIVERY_METHOD_OPTIONS,
  GUARANTEE_FORM_EFFECTIVE_FROM_TYPE_OPTIONS,
  GUARANTEE_FORM_RULE_OPTIONS,
  GUARANTEE_FORM_REQUIRED_FIELDS,
  GUARANTEE_FORM_SUBORDINATION_RULE_OPTIONS,
  NON_RESIDENT_BENEFICIARY_IDENTIFY_CODE,
  PRINCIPAL_REQUIRED_FIELDS,
  PRINCIPAL_CONSTITUENT_DOCUMENTS_CHANGES_OPTIONS,
  PRINCIPAL_OBLIGATION_DETAILS_OPTIONS,
} from '../../rootSection/constants.ts';
import {
  GuaranteesBaseRelationsFieldName,
  GuaranteesBeneficiaryFieldName,
  GuaranteesFormFieldName,
  GuaranteesPrincipalConstituentDocumentsChangesOption,
  GuaranteesPrincipalFieldName,
  type TGuaranteesPrincipalSectionSource,
} from '../../rootSection/types.ts';

const getStringValue = (value: unknown): string => String(value ?? '').trim();
const getBooleanValue = (value: unknown): boolean => (typeof value === 'boolean' ? value : false);
const getAutocompleteOptionValue = (value: unknown): string => {
  if (value && typeof value === 'object' && 'value' in value) {
    return getStringValue(value.value);
  }

  return '';
};
const findOption = <TOption extends TAutocompleteOption>(
  options: readonly TOption[],
  value: unknown
): TOption | null => {
  const normalizedValue = getStringValue(value);

  return options.find((option) => option.value === normalizedValue) ?? null;
};
const findPrincipalConstituentDocumentsChangesOption = (
  value: GuaranteesPrincipalConstituentDocumentsChangesOption
) =>
  PRINCIPAL_CONSTITUENT_DOCUMENTS_CHANGES_OPTIONS.find((option) => option.value === value) ??
  null;

function toResolverErrors<TValues extends Record<string, unknown>>(
  errors: Partial<Record<keyof TValues, string>>
): FieldErrors<TValues> {
  return Object.entries(errors).reduce<FieldErrors<TValues>>((accumulator, [name, message]) => {
    if (!message) {
      return accumulator;
    }

    return {
      ...accumulator,
      [name]: {
        type: 'manual',
        message,
      },
    };
  }, {});
}

export const getGuaranteesPrincipalInitialValues = (
  principal?: TGuaranteesPrincipalSectionSource | null
): TGuaranteesPrincipal => ({
  [GuaranteesPrincipalFieldName.PrincipalIdentifyCode]: getStringValue(
    principal?.principal_identify_code ?? principal?.identifyCode
  ),
  [GuaranteesPrincipalFieldName.PrincipalFullName]: getStringValue(
    principal?.principal_full_name ?? principal?.fullName
  ),
  [GuaranteesPrincipalFieldName.PrincipalShortName]: getStringValue(
    principal?.principal_short_name ?? principal?.shortName
  ),
  [GuaranteesPrincipalFieldName.PrincipalLocation]: getStringValue(
    principal?.principal_location ?? principal?.location ?? principal?.address
  ),
  [GuaranteesPrincipalFieldName.PrincipalNameAddressGuaranteeLang]: getStringValue(
    principal?.principal_name_address_guarantee_lang ??
      principal?.nameAddressGuaranteeLang ??
      principal?.displayNameInGuaranteeLanguage
  ),
  [GuaranteesPrincipalFieldName.PrincipalContactFullName]: getStringValue(
    principal?.principal_contact_full_name ??
      principal?.contactFullName ??
      principal?.contactPersonFullName
  ),
  [GuaranteesPrincipalFieldName.PrincipalContactEmail]: getStringValue(
    principal?.principal_contact_email ?? principal?.contactEmail
  ),
  [GuaranteesPrincipalFieldName.PrincipalContactPhone]: getStringValue(
    principal?.principal_contact_phone ?? principal?.contactPhone
  ),
  [GuaranteesPrincipalFieldName.PrincipalAuthorizedPersonPosition]: getStringValue(
    principal?.principal_authorized_person_position ?? principal?.authorizedPersonPosition
  ),
  [GuaranteesPrincipalFieldName.PrincipalAuthorizedPersonName]: getStringValue(
    principal?.principal_authorized_person_name ??
      principal?.authorizedPersonName ??
      principal?.authorizedPersonFullName
  ),
  [GuaranteesPrincipalFieldName.PrincipalHasChangesInConstituentDocuments]: getBooleanValue(
    principal?.hasChangesInConstituentDocuments
  )
    ? findPrincipalConstituentDocumentsChangesOption(
        GuaranteesPrincipalConstituentDocumentsChangesOption.YES
      )
    : findPrincipalConstituentDocumentsChangesOption(
        GuaranteesPrincipalConstituentDocumentsChangesOption.NO
      ),
});

const principalEmailValidation = Yup.string()
  .required(errorMessages.FIELD_REQUIRED)
  .email(errorMessages.INVALID_EMAIL_FORMAT)
  .test(
    'blocked-domain',
    errorMessages.BLOCKED_EMAIL_DOMAIN,
    function (value: string | undefined): boolean {
      if (!value || !value.includes('@')) return true;
      const domain = value.split('@')[1];
      return !blockedDomains.some(
        (blocked: string) => domain === blocked || domain?.endsWith(`.${blocked}`)
      );
    }
  );

const validatePrincipalEmail = async (
  values: TGuaranteesPrincipal,
  errors: Partial<Record<keyof TGuaranteesPrincipal, string>>
) => {
  if (errors.principal_contact_email) {
    return;
  }

  try {
    await principalEmailValidation.validate(values.principal_contact_email);
  } catch (error) {
    errors.principal_contact_email =
      error instanceof Error ? error.message : errorMessages.INVALID_EMAIL_FORMAT;
  }
};

const validatePrincipalPhone = async (
  values: TGuaranteesPrincipal,
  errors: Partial<Record<keyof TGuaranteesPrincipal, string>>
) => {
  if (errors.principal_contact_phone) {
    return;
  }

  try {
    await phoneValidation.validate(values.principal_contact_phone);
  } catch (error) {
    errors.principal_contact_phone =
      error instanceof Error ? error.message : errorMessages.INVALID_INPUT_DATA;
  }
};

export const validateGuaranteesPrincipalForm = async (
  values: TGuaranteesPrincipal
): Promise<Partial<Record<keyof TGuaranteesPrincipal, string>>> => {
  const errors: Partial<Record<keyof TGuaranteesPrincipal, string>> = {};

  PRINCIPAL_REQUIRED_FIELDS.forEach((fieldName) => {
    if (!getStringValue(values[fieldName])) {
      errors[fieldName] = errorMessages.FIELD_REQUIRED;
    }
  });

  await validatePrincipalEmail(values, errors);
  await validatePrincipalPhone(values, errors);

  return errors;
};

export const guaranteesPrincipalSectionResolver: Resolver<TGuaranteesPrincipal> = async (
  values
) => {
  const errors = await validateGuaranteesPrincipalForm(values);

  return {
    values: Object.keys(errors).length ? {} : values,
    errors: toResolverErrors<TGuaranteesPrincipal>(errors),
  };
};

export const buildGuaranteesPrincipalPayload = (
  values: TGuaranteesPrincipal
): TPatchGuaranteesPrincipalPayload => ({
  identifyCode: getStringValue(values.principal_identify_code),
  fullName: getStringValue(values.principal_full_name),
  shortName: getStringValue(values.principal_short_name),
  location: getStringValue(values.principal_location),
  nameAddressGuaranteeLang: getStringValue(values.principal_name_address_guarantee_lang),
  contactFullName: getStringValue(values.principal_contact_full_name),
  contactEmail: getStringValue(values.principal_contact_email),
  contactPhone: getStringValue(values.principal_contact_phone),
  authorizedPersonPosition: getStringValue(values.principal_authorized_person_position),
  authorizedPersonName: getStringValue(values.principal_authorized_person_name),
  hasChangesInConstituentDocuments:
    values.hasChangesInConstituentDocuments?.value ===
    GuaranteesPrincipalConstituentDocumentsChangesOption.YES,
});

export const getGuaranteesBeneficiaryInitialValues = (
  beneficiary?: (Partial<TGuaranteesBeneficiary> & Record<string, unknown>) | null
): TGuaranteesBeneficiary => {
  const identifyCode = getStringValue(
    beneficiary?.identifyCode
  );
  const countryName = getStringValue(
    beneficiary?.countryName ?? beneficiary?.beneficiaryResidenceCountry?.label
  );
  const countryId = getStringValue(
    beneficiary?.countryId ??
      (countryName ? '' : beneficiary?.beneficiaryResidenceCountry?.value)
  );

  return {
    [GuaranteesBeneficiaryFieldName.BeneficiaryName]: getStringValue(beneficiary?.name),
    [GuaranteesBeneficiaryFieldName.BeneficiaryIdentifyCode]: identifyCode,
    [GuaranteesBeneficiaryFieldName.BeneficiaryLocation]: getStringValue(
      beneficiary?.address ?? beneficiary?.location
    ),
    [GuaranteesBeneficiaryFieldName.BeneficiaryNameGuaranteeLang]: getStringValue(
      beneficiary?.displayNameInGuaranteeLanguage
    ),
    [GuaranteesBeneficiaryFieldName.BeneficiaryGuarantorBank]: getStringValue(
      beneficiary?.guarantorBank
    ),
    [GuaranteesBeneficiaryFieldName.BeneficiaryBank]: getStringValue(
      beneficiary?.beneficiaryBank ?? beneficiary?.bank
    ),
    [GuaranteesBeneficiaryFieldName.AdvisingBank]: getStringValue(beneficiary?.advisingBank),
    [GuaranteesBeneficiaryFieldName.IsNonResidentBeneficiary]:
      identifyCode === NON_RESIDENT_BENEFICIARY_IDENTIFY_CODE,
    [GuaranteesBeneficiaryFieldName.BeneficiaryResidenceCountry]: countryId
      ? { value: countryId, label: countryName || countryId }
      : countryName
        ? { value: countryName, label: countryName }
        : null,
    countryName,
    countryId,
  };
};

export const validateGuaranteesBeneficiaryForm = async (
  values: TGuaranteesBeneficiary
): Promise<Partial<Record<keyof TGuaranteesBeneficiary, string>>> => {
  const errors: Partial<Record<keyof TGuaranteesBeneficiary, string>> = {};

  BENEFICIARY_REQUIRED_FIELDS.forEach((fieldName) => {
    const value = values[fieldName];
    const normalizedValue =
      fieldName === GuaranteesBeneficiaryFieldName.BeneficiaryResidenceCountry
        ? getAutocompleteOptionValue(value)
        : getStringValue(value);

    if (!normalizedValue) {
      errors[fieldName] = errorMessages.FIELD_REQUIRED;
    }
  });

  if (
    getStringValue(
      values[GuaranteesBeneficiaryFieldName.BeneficiaryNameGuaranteeLang]
    ).length > BENEFICIARY_NAME_GUARANTEE_LANG_MAX_LENGTH
  ) {
    errors[GuaranteesBeneficiaryFieldName.BeneficiaryNameGuaranteeLang] = errorMessages.MAX_LENGTH(
      BENEFICIARY_NAME_GUARANTEE_LANG_MAX_LENGTH
    );
  }

  return errors;
};

export const guaranteesBeneficiarySectionResolver: Resolver<TGuaranteesBeneficiary> = async (
  values
) => {
  const errors = await validateGuaranteesBeneficiaryForm(values);

  return {
    values: Object.keys(errors).length ? {} : values,
    errors: toResolverErrors<TGuaranteesBeneficiary>(errors),
  };
};

export const buildGuaranteesBeneficiaryPayload = (
  values: TGuaranteesBeneficiary
): TPatchGuaranteesBeneficiaryPayload => ({
  name: getStringValue(values.name),
  identifyCode: values.isNonResidentBeneficiary
    ? NON_RESIDENT_BENEFICIARY_IDENTIFY_CODE
    : getStringValue(values.identifyCode),
  location: getStringValue(values.address),
  nameGuaranteeLang: getStringValue(values.displayNameInGuaranteeLanguage),
  guarantorBank: getStringValue(values.guarantorBank),
  bank: getStringValue(values.beneficiaryBank),
  advisingBank: getStringValue(values.advisingBank),
  countryId: getAutocompleteOptionValue(values.beneficiaryResidenceCountry),
  countryName: getStringValue(values.beneficiaryResidenceCountry?.label),
});

const toDateInputValue = (value?: string | null): string => {
  const normalizedValue = getStringValue(value);

  if (!normalizedValue) {
    return '';
  }

  if (isValidDateFormat(normalizedValue)) {
    return normalizedValue;
  }

  return formatDateForDisplay(normalizedValue) || normalizedValue;
};

const isWeekendDate = (value: string): boolean => {
  const date = parseDate(value);

  if (!date) {
    return false;
  }

  const day = date.getDay();

  return day === 0 || day === 6;
};

const getNullableOtherValue = (option: TAutocompleteOption | null, value: string): string | null =>
  option?.value === 'OTHER' ? getStringValue(value) : null;

export const getGuaranteesFormInitialValues = (
  guaranteeForm?: TGuaranteesForm | null
): TGuaranteesFormValues => ({
  [GuaranteesFormFieldName.IssuePlace]: getStringValue(guaranteeForm?.issuePlace),
  [GuaranteesFormFieldName.IssueDate]: toDateInputValue(guaranteeForm?.issueDate),
  [GuaranteesFormFieldName.DocumentsLanguage]: getStringValue(guaranteeForm?.documentLanguage),
  [GuaranteesFormFieldName.DemandPresentationForm]: getStringValue(
    guaranteeForm?.demandPresentationFormat
  ),
  [GuaranteesFormFieldName.DemandPresentationPlace]: getStringValue(
    guaranteeForm?.demandPresentationPlace
  ),
  [GuaranteesFormFieldName.SupportingDocuments]: getStringValue(
    guaranteeForm?.supportingDocuments
  ),
  [GuaranteesFormFieldName.DeliveryMethod]: findOption(
    GUARANTEE_FORM_DELIVERY_METHOD_OPTIONS,
    guaranteeForm?.deliveryMethod ?? GuaranteesDeliveryMethod.PRINCIPAL_EMAIL
  ),
  [GuaranteesFormFieldName.DeliveryMethodOther]: getStringValue(
    guaranteeForm?.deliveryMethodOther
  ),
  [GuaranteesFormFieldName.GuaranteeFormRule]: findOption(
    GUARANTEE_FORM_RULE_OPTIONS,
    guaranteeForm?.guaranteeFormRule ?? GuaranteesFormRule.APPLICATION_P7
  ),
  [GuaranteesFormFieldName.GuaranteeFormRuleOther]: getStringValue(
    guaranteeForm?.guaranteeFormRuleOther
  ),
  [GuaranteesFormFieldName.BankCommissionsRule]: findOption(
    GUARANTEE_FORM_BANK_COMMISSIONS_RULE_OPTIONS,
    guaranteeForm?.bankCommissionsRule ?? GuaranteesBankCommissionsRule.PRINCIPAL_PAYS_ALL
  ),
  [GuaranteesFormFieldName.BankCommissionsRuleOther]: getStringValue(
    guaranteeForm?.bankCommissionsRuleOther
  ),
  [GuaranteesFormFieldName.SubordinationRule]: findOption(
    GUARANTEE_FORM_SUBORDINATION_RULE_OPTIONS,
    guaranteeForm?.subordinationRule
  ),
  [GuaranteesFormFieldName.SubordinationRuleOther]: getStringValue(
    guaranteeForm?.subordinationRuleOther
  ),
  [GuaranteesFormFieldName.EffectiveFromType]: findOption(
    GUARANTEE_FORM_EFFECTIVE_FROM_TYPE_OPTIONS,
    guaranteeForm?.effectiveFromType
  ),
  [GuaranteesFormFieldName.EffectiveFromDate]: toDateInputValue(guaranteeForm?.effectiveFromDate),
});

export const validateGuaranteesForm = (
  values: TGuaranteesFormValues
): Partial<Record<keyof TGuaranteesFormValues, string>> => {
  const errors: Partial<Record<keyof TGuaranteesFormValues, string>> = {};

  GUARANTEE_FORM_REQUIRED_FIELDS.forEach((fieldName) => {
    if (!getStringValue(values[fieldName])) {
      errors[fieldName] = errorMessages.FIELD_REQUIRED;
    }
  });

  const effectiveFromType = values.effectiveFromType?.value;

  if (effectiveFromType === GuaranteesEffectiveFromType.OTHER_DATE) {
    if (!getStringValue(values.effectiveFromDate)) {
      errors.effectiveFromDate = errorMessages.FIELD_REQUIRED;
    } else if (!isValidDateFormat(values.effectiveFromDate)) {
      errors.effectiveFromDate = errorMessages.CORRECT_DATE_FORMAT;
    } else if (isWeekendDate(values.effectiveFromDate)) {
      errors.effectiveFromDate = errorMessages.INVALID_DATE;
    }
  }

  if (!values.deliveryMethod?.value) {
    errors.deliveryMethod = errorMessages.OPTION_REQUIRED;
  }

  if (
    values.deliveryMethod?.value === GuaranteesDeliveryMethod.OTHER &&
    !getStringValue(values.deliveryMethodOther)
  ) {
    errors.deliveryMethodOther = errorMessages.FIELD_REQUIRED;
  }

  if (!values.guaranteeFormRule?.value) {
    errors.guaranteeFormRule = errorMessages.OPTION_REQUIRED;
  }

  if (
    values.guaranteeFormRule?.value === GuaranteesFormRule.OTHER &&
    !getStringValue(values.guaranteeFormRuleOther)
  ) {
    errors.guaranteeFormRuleOther = errorMessages.FIELD_REQUIRED;
  }

  if (!values.bankCommissionsRule?.value) {
    errors.bankCommissionsRule = errorMessages.OPTION_REQUIRED;
  }

  if (
    values.bankCommissionsRule?.value === GuaranteesBankCommissionsRule.OTHER &&
    !getStringValue(values.bankCommissionsRuleOther)
  ) {
    errors.bankCommissionsRuleOther = errorMessages.FIELD_REQUIRED;
  }

  if (
    values.subordinationRule?.value === GuaranteesSubordinationRule.OTHER &&
    !getStringValue(values.subordinationRuleOther)
  ) {
    errors.subordinationRuleOther = errorMessages.FIELD_REQUIRED;
  }

  return errors;
};

export const guaranteesFormSectionResolver: Resolver<TGuaranteesFormValues> = async (values) => {
  const errors = validateGuaranteesForm(values);

  return {
    values: Object.keys(errors).length ? {} : values,
    errors: toResolverErrors<TGuaranteesFormValues>(errors),
  };
};

export const buildGuaranteesFormPayload = (
  values: TGuaranteesFormValues
): TPatchGuaranteesFormPayload => {
  const payload: TPatchGuaranteesFormPayload = {
    deliveryMethod: values.deliveryMethod?.value as GuaranteesDeliveryMethod,
    deliveryMethodOther: getNullableOtherValue(
      values.deliveryMethod,
      values.deliveryMethodOther
    ),
    guaranteeFormRule: values.guaranteeFormRule?.value as GuaranteesFormRule,
    guaranteeFormRuleOther: getNullableOtherValue(
      values.guaranteeFormRule,
      values.guaranteeFormRuleOther
    ),
    bankCommissionsRule: values.bankCommissionsRule?.value as GuaranteesBankCommissionsRule,
    bankCommissionsRuleOther: getNullableOtherValue(
      values.bankCommissionsRule,
      values.bankCommissionsRuleOther
    ),
    subordinationRuleOther: getNullableOtherValue(
      values.subordinationRule,
      values.subordinationRuleOther
    ),
    effectiveFromDate:
      values.effectiveFromType?.value === GuaranteesEffectiveFromType.OTHER_DATE
        ? (formatDateReverse(values.effectiveFromDate) ?? '')
        : null,
  };
  const optionalStringFields = [
    ['demandPresentationForm', values.demandPresentationForm],
    ['supportingDocuments', values.supportingDocuments],
  ] as const;

  optionalStringFields.forEach(([key, value]) => {
    const normalizedValue = value.trim();

    if (normalizedValue) {
      payload[key] = normalizedValue;
    }
  });

  if (values.subordinationRule?.value) {
    payload.subordinationRule = values.subordinationRule.value as GuaranteesSubordinationRule;
  }

  if (values.effectiveFromType?.value) {
    payload.effectiveFromType = values.effectiveFromType.value as GuaranteesEffectiveFromType;
  }

  return payload;
};

const getGuaranteesBaseRelationsDocumentValue = (
  baseRelations?: TGuaranteesBaseRelations | null
): string =>
  getStringValue(
    baseRelations?.baseRelationsDocument ??
      baseRelations?.relationsDocument ??
      baseRelations?.base_relations_document
  );

export const getGuaranteesBaseRelationsInitialValues = (
  baseRelations?: TGuaranteesBaseRelations | null,
  isProzorroTender = false
): TGuaranteesBaseRelationsValues => ({
  [GuaranteesBaseRelationsFieldName.RelationsDocument]: isProzorroTender
    ? BASE_RELATIONS_PROZORRO_VALUE
    : getGuaranteesBaseRelationsDocumentValue(baseRelations),
  [GuaranteesBaseRelationsFieldName.PrincipalObligationDetails]: findOption(
    PRINCIPAL_OBLIGATION_DETAILS_OPTIONS,
    baseRelations?.principalObligationDetails
  ),
});

export const guaranteesBaseRelationsSectionResolver: Resolver<
  TGuaranteesBaseRelationsValues
> = async (values) => {
  const errors: Partial<Record<keyof TGuaranteesBaseRelationsValues, string>> = {};

  if (!getAutocompleteOptionValue(values.principalObligationDetails)) {
    errors.principalObligationDetails = errorMessages.OPTION_REQUIRED;
  }

  return {
    values: Object.keys(errors).length ? {} : values,
    errors: toResolverErrors<TGuaranteesBaseRelationsValues>(errors),
  };
};

export const buildGuaranteesBaseRelationsPayload = (
  values: TGuaranteesBaseRelationsValues
): TPatchGuaranteesBaseRelationsPayload => ({
  baseRelationsDocument: values.relationsDocument.trim(),
  principalObligationDetails: values.principalObligationDetails
    ?.value as GuaranteesPrincipalObligationDetails,
});
